import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useCartActions } from "../../hooks/useCartActions";

import { fetchMedicinesByStore } from "../../services/Medicine/GetMedicineByStore";
import { fetchMedicineStoresData } from "../../services/Medicine/MedicineStore";

import MedicineDetails from "./MedicineDetails";

// ✅ SWIPER
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

export default function GetMedicineByStore() {
  const { companyId } = useParams();
  const storeId = Number(companyId);

  const [store, setStore] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedMedicineId, setSelectedMedicineId] = useState(null);

  const reduxCart = useSelector((state) => state.cart.items);

  // ================= CART HOOK =================
  const { addWithReplaceCheck, removeItem } = useCartActions();

  // ================= CART QTY =================
  const getCartQty = (id) => {
    return (
      reduxCart.find(
        (i) =>
          Number(i.productId) === Number(id) &&
          Number(i.categoryId) === 1
      )?.quantity || 0
    );
  };

  // ================= STORE =================
  useEffect(() => {
    if (!storeId) return;

    const loadStore = async () => {
      try {
        const stores = await fetchMedicineStoresData();
        const selected = stores.find((s) => Number(s.id) === storeId);
        setStore(selected || null);
      } catch (err) {
        console.log("STORE ERROR:", err);
        setError("Failed to load store");
      }
    };

    loadStore();
  }, [storeId]);

  // ================= MEDICINES =================
  useEffect(() => {
    const loadMedicines = async () => {
      try {
        setLoading(true);

        const data = await fetchMedicinesByStore(storeId, 1, 10);

        console.log("🧪 MEDICINES API:", data);

        setMedicines(Array.isArray(data) ? data : []);
      } catch (err) {
        console.log("MEDICINE ERROR:", err);
        setError("Failed to load medicines");
      } finally {
        setLoading(false);
      }
    };

    if (storeId) loadMedicines();
  }, [storeId]);

  // ================= ADD =================
  const handleAdd = (med) => {
    const item = {
      productId: med?.id,
      categoryId: 1,
      name: med?.product,
      price: Number(med?.salePrice || 0),
      discount: Number(med?.discount || 0),
      storeId: storeId,
    };

    console.log("➕ ADD TO CART:", item);

    if (!item.productId) {
      console.error("❌ productId missing", med);
      return;
    }

    addWithReplaceCheck(item);
  };

  // ================= REMOVE =================
  const handleRemove = (med) => {
    const item = {
      productId: med?.id,
      categoryId: 1,
      name: med?.product,
      price: Number(med?.salePrice || 0),
      discount: Number(med?.discount || 0),
      storeId: storeId,
    };

    console.log("➖ REMOVE FROM CART:", item);

    removeItem(item);
  };

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ================= ERROR =================
  if (error) {
    return <div className="text-center text-red-500 mt-5">{error}</div>;
  }

  return (
    <div className="w-full bg-gray-50 min-h-screen pb-20">

      {/* STORE HEADER */}
      <div className="bg-white p-4 shadow-md rounded-xl mx-3 flex gap-4 items-center mt-5">

        <img
          src={
            store?.image ||
            "https://online-tech.in/images/Companymedicine/d3ff1210-db9a-48ad-ad4f-74b568656d3f.png"
          }
          alt="store"
          className="h-[110px] w-[140px] object-contain rounded-lg border bg-gray-50"
          onError={(e) => {
            e.target.src =
              "https://online-tech.in/images/Companymedicine/d3ff1210-db9a-48ad-ad4f-74b568656d3f.png";
          }}
        />

        <div className="flex-1">
          <h2 className="text-xl font-bold">
            {store?.name || "Medical Store"}
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            {store?.address}
          </p>
        </div>
      </div>

      {/* TITLE */}
      <div className="p-3 mt-5">
        <h4 className="text-center text-2xl font-bold mb-6 text-red-600">
          Available Medicines
        </h4>

        {medicines.length === 0 && (
          <div className="text-center text-gray-500">
            ❌ No medicines found
          </div>
        )}

        {/* SWIPER */}
        <Swiper
          spaceBetween={10}
          slidesPerView={2}
          breakpoints={{
            640: { slidesPerView: 3 },
            1024: { slidesPerView: 5 },
          }}
        >
          {medicines.map((med) => {
            const qty = getCartQty(med.id);

            const mrp = Number(med.mrpPrice) || 0;
            const price = Number(med.salePrice) || 0;

            return (
              <SwiperSlide key={med.id}>
                <div className="bg-white border rounded-md shadow-sm p-2">

                  {/* IMAGE */}
                  <div
                    onClick={() => setSelectedMedicineId(med.id)}
                    className="h-[70px] flex items-center justify-center bg-gray-50 rounded-md cursor-pointer"
                  >
                    <img
                      src={
                        med?.image
                          ? `https://online-tech.in/images/Companymedicine/${med.image}`
                          : "https://online-tech.in/images/Companymedicine/d3ff1210-db9a-48ad-ad4f-74b568656d3f.png"
                      }
                      alt={med.product}
                      className="max-h-full object-contain"
                      onError={(e) => {
                        e.target.src =
                          "https://online-tech.in/images/Companymedicine/d3ff1210-db9a-48ad-ad4f-74b568656d3f.png";
                      }}
                    />
                  </div>

                  {/* NAME */}
                  <p className="text-xs text-center mt-1 line-clamp-2 min-h-[30px]">
                    {med.product}
                  </p>

                  {/* PRICE + CART */}
                  <div className="flex justify-between items-center mt-2">

                    <div>
                      <span className="text-sm font-bold text-green-600">
                        ₹{price}
                      </span>

                      {mrp > price && (
                        <div className="text-[10px] line-through text-gray-400">
                          ₹{mrp}
                        </div>
                      )}
                    </div>

                    {/* CART BUTTON */}
                    {qty === 0 ? (
                      <button
                        onClick={() => handleAdd(med)}
                        className="px-2 py-1 text-[11px] bg-blue-100 text-blue-600 border rounded"
                      >
                        Add
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">

                        <button
                          onClick={() => handleRemove(med)}
                          className="w-6 h-6 rounded bg-red-100 text-red-600"
                        >
                          -
                        </button>

                        <span className="text-sm font-semibold">
                          {qty}
                        </span>

                        <button
                          onClick={() => handleAdd(med)}
                          className="w-6 h-6 rounded bg-green-100 text-green-600"
                        >
                          +
                        </button>

                      </div>
                    )}

                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {/* DRAWER */}
      {selectedMedicineId && (
        <MedicineDetails
          id={selectedMedicineId}
          onClose={() => setSelectedMedicineId(null)}
        />
      )}
    </div>
  );
}